import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';
import { Spinner } from './Spinner';

const buttonVariants = cva(
  // Base styles
  `inline-flex items-center justify-center gap-2
   font-bold uppercase tracking-wider
   border-2 border-md-graphite rounded
   transition-all duration-120 cursor-pointer
   disabled:opacity-50 disabled:cursor-not-allowed`,
  {
    variants: {
      variant: {
        primary: `
          bg-md-sunbeam text-md-graphite
          hover:translate-x-0.5 hover:-translate-y-0.5
          hover:shadow-hover
          disabled:hover:translate-x-0 disabled:hover:translate-y-0
          disabled:hover:shadow-none
        `,
        secondary: `
          bg-md-fog text-md-ink
          hover:bg-md-cream
          disabled:hover:bg-md-fog
        `,
        ghost: `
          bg-transparent border-transparent
          hover:bg-md-fog
          disabled:hover:bg-transparent
        `,
        danger: `
          bg-md-error text-white border-md-error
          hover:bg-red-600
          disabled:hover:bg-md-error
        `,
      },
      size: {
        sm: 'px-3 py-1.5 text-xs',
        md: 'px-4 py-2 text-sm',
        lg: 'px-6 py-3 text-base',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export function Button({
  className,
  variant,
  size,
  fullWidth,
  loading,
  disabled,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(buttonVariants({ variant, size, fullWidth }), className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Spinner size="sm" />}
      {children}
    </button>
  );
}
